export const enum ShipClass {
  SCSC_IV = 0,
  SCSC_III = 1,
  SCSC_II = 2,
  SCSC_I = 3,
  SCSSC = 4,
}

export const SHIP_CLASS_NAMES: readonly string[] = [
  'SCSC IV', 'SCSC III', 'SCSC II', 'SCSC I', 'SCSSC',
];

/** Hologramm-Farben pro Klasse: SCSSC=harmlos(cyan) → SCSC_I=bedrohlich(rot). */
export const SHIP_CLASS_COLORS: readonly (readonly [number, number, number])[] = [
  [0.35, 0.65, 1.0],   // SCSC_IV  — kühl, niedrig
  [0.85, 0.7, 0.2],    // SCSC_III — amber, Vorsicht
  [0.95, 0.4, 0.15],   // SCSC_II  — orange, gefährlich
  [0.95, 0.15, 0.15],  // SCSC_I   — rot, maximale Bedrohung
  [0.3, 0.85, 0.9],    // SCSSC    — sanft cyan, harmlos
];

export const enum ShipType {
  Scout = 0,
  Fighter = 1,
  Bucaneer = 2,
  Battleship = 3,
  ICSS = 4,
  Carrier = 5,
  Receptor = 6,
  Colonyship = 7,
}

export const SHIP_TYPE_COUNT = 8;

export interface ShipTypeDef {
  readonly type: ShipType;
  readonly shipClass: ShipClass;
  readonly name: string;
  readonly modelUrl: string;
  scale: number;
  footprint: number;
  readonly color: readonly [number, number, number];
  readonly yawOffset: number;
  readonly displayMax: number;
  readonly speed: number;
  readonly warpCapable: boolean;
  readonly allowSunInNormal: boolean;
  /** Per-carrier warp transport capacity, indexed by ShipType. null = no carry ability. */
  readonly warpCarry: readonly number[] | null;
  /** Build time in seconds. SYNC mit supabase/functions/build-ship/index.ts. */
  readonly buildTime: number;
}

export const SHIP_TYPE_DEFS: readonly ShipTypeDef[] = [
  {
    type: ShipType.Scout, shipClass: ShipClass.SCSC_IV,
    name: 'Scout', modelUrl: '/models/ships/scout/Scout_Alpha_1_C.glb',
    scale: 0.0015, footprint: 0.090, color: SHIP_CLASS_COLORS[ShipClass.SCSC_IV], yawOffset: -Math.PI * 0.5,
    displayMax: 8, speed: 1.5, warpCapable: false, allowSunInNormal: false,
    warpCarry: null, buildTime: 30,
  },
  {
    type: ShipType.Fighter, shipClass: ShipClass.SCSC_III,
    name: 'Fighter', modelUrl: '/models/ships/fighter/Fighter_Alpha_1_C.glb',
    scale: 0.0005, footprint: 0.020, color: SHIP_CLASS_COLORS[ShipClass.SCSC_III], yawOffset: -Math.PI * 0.5,
    displayMax: 12, speed: 1.3, warpCapable: false, allowSunInNormal: false,
    warpCarry: null, buildTime: 60,
  },
  {
    type: ShipType.Bucaneer, shipClass: ShipClass.SCSC_III,
    name: 'Bucaneer', modelUrl: '/models/ships/bucaneer/Bucaneer_Alpha_1_C.glb',
    scale: 0.0006, footprint: 0.020, color: SHIP_CLASS_COLORS[ShipClass.SCSC_III], yawOffset: -Math.PI * 0.5,
    displayMax: 10, speed: 1.2, warpCapable: false, allowSunInNormal: false,
    warpCarry: null, buildTime: 90,
  },
  {
    type: ShipType.Battleship, shipClass: ShipClass.SCSC_II,
    name: 'Battleship', modelUrl: '/models/ships/battleship/Battleship_Alpha_1_C.glb',
    scale: 0.0013, footprint: 0.210, color: SHIP_CLASS_COLORS[ShipClass.SCSC_II], yawOffset: -Math.PI * 0.5,
    displayMax: 6, speed: 0.7, warpCapable: true, allowSunInNormal: false,
    warpCarry: null, buildTime: 300,
  },
  {
    type: ShipType.ICSS, shipClass: ShipClass.SCSC_I,
    name: 'ICSS', modelUrl: '/models/ships/icss/ICSS_Alpha_1_C.glb',
    scale: 0.0013, footprint: 0.440, color: SHIP_CLASS_COLORS[ShipClass.SCSC_I], yawOffset: -Math.PI * 0.5,
    displayMax: 3, speed: 0.5, warpCapable: true, allowSunInNormal: false,
    warpCarry: null, buildTime: 600,
  },
  {
    type: ShipType.Carrier, shipClass: ShipClass.SCSSC,
    name: 'Carrier', modelUrl: '/models/ships/carrier/Carrier_Alpha_1_C.glb',
    scale: 0.0020, footprint: 0.125, color: SHIP_CLASS_COLORS[ShipClass.SCSSC], yawOffset: -Math.PI * 0.5,
    displayMax: 4, speed: 0.5, warpCapable: true, allowSunInNormal: false,
    warpCarry: [5, 20, 10, 0, 0, 0, 0, 0], buildTime: 900,
  },
  {
    type: ShipType.Receptor, shipClass: ShipClass.SCSSC,
    name: 'Receptor', modelUrl: '/models/ships/receptor/Receptor_Alpha_1_C.glb',
    scale: 0.0022, footprint: 0.310, color: SHIP_CLASS_COLORS[ShipClass.SCSSC], yawOffset: -Math.PI * 0.5,
    displayMax: 4, speed: 0.6, warpCapable: false, allowSunInNormal: true,
    warpCarry: null, buildTime: 720,
  },
  {
    type: ShipType.Colonyship, shipClass: ShipClass.SCSSC,
    name: 'Colonyship', modelUrl: '/models/ships/colonyship/Colonyship_Alpha_1_C.glb',
    scale: 0.0015, footprint: 0.200, color: SHIP_CLASS_COLORS[ShipClass.SCSSC], yawOffset: -Math.PI * 0.5,
    displayMax: 1, speed: 0.4, warpCapable: true, allowSunInNormal: false,
    warpCarry: null, buildTime: 1200,
  },
];

export function getShipTypeDef(type: ShipType): ShipTypeDef {
  return SHIP_TYPE_DEFS[type];
}
